import { Button, Flex, Form, Switch } from "antd";
import { KeyboardEvent, useEffect, useRef } from "react";
import styles from "./MinerDataFields.module.scss";
import { useMinerDataFieldsControl } from "./useMinerDataFieldsControl";

import origEditIcon from "@assets/redesign/icons/inputEdit.svg";
import close from "@assets/redesign/icons/x.svg";
import Icon from "@components/global/new/Icon/Icon";
import FormInput from "./FormInput/FormInput";
import wlpEditIcon from "@components/WLP202603/assets/redesign/icons/inputEdit.svg";
import { WLP202603 } from "../../../../../../config";
import { PlusOutlined } from "@ant-design/icons";

const editIcon = WLP202603 ? wlpEditIcon : origEditIcon;

export default function MinerDataFields() {

  const control = useMinerDataFieldsControl();

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (control.isEdit) {
      ref.current?.focus();
    }
  }, [control.isEdit]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      control.requestReset();
    }
    if (e.key === "Enter" && e.ctrlKey) {
      control.formRef.current?.submit();
    }
  };

  return (
    <Flex
      align="start"
      gap={6}
      wrap
      className={styles.wrapper}
      id={"data-fields"}
      ref={ref}
      tabIndex={100}
      // onBlur={(e) => {
      //   if (!control.isEdit) return;
      //   if (e.currentTarget.contains(e.relatedTarget)) return;
      //   control.requestReset();
      // }}
      onKeyDown={onKeyDown}
    >
      <Form
        ref={control.formRef}
        onFinish={control.onSave}
        //disabled={!control.isEdit}
        tabIndex={-1}
        className={styles.form}
      >
        <Icon
          src={control.isEdit ? close : editIcon}
          className={styles.edit_icon}
          onClick={control.onEdit}
          tabIndex={-1}
        />
        <Form.Item
          name="ip"
          validateStatus={control.ipError ? "error" : undefined}
          help={
            control.ipError &&
            control.formatMessage({
              id: "machines.more.settings.ip.validation",
            })
          }
        >
          <FormInput
            inititalValue={control.data.ip}
            label={control.formatMessage({ id: "machines.more.settings.ip" })}
            onInputChecker={control.onCheckData}
            type="ip"
            disabled={!control.isEdit}
            formRef={control.formRef}
          />
        </Form.Item>
        <Form.Item
          name="mac"
          validateStatus={control.macError ? "error" : undefined}
          help={
            control.macError &&
            control.formatMessage({
              id: "machines.more.settings.mac.validation",
            })
          }
        >
          <FormInput
            inititalValue={control.data.mac}
            label={control.formatMessage({ id: "machines.more.settings.mac" })}
            onInputChecker={control.onCheckData}
            type="mac"
            disabled={!control.isEdit}
            formRef={control.formRef}
          />
        </Form.Item>
        <Form.Item
          name="sn"
          style={{
            minWidth: 300,
          }}
        >
          <FormInput
            inititalValue={control.data.sn}
            label={control.formatMessage({ id: "machines.more.serial.title" })}
            onInputChecker={control.onCheckData}
            type="sn"
            disabled={!control.isEdit}
            formRef={control.formRef}
          />
        </Form.Item>
        <Form.Item
          name="port"
          validateStatus={control.portError ? "error" : undefined}
        >
          <FormInput
            inititalValue={control.data.port}
            label={control.formatMessage({ id: "machines.more.settings.port" })}
            onInputChecker={control.onCheckData}
            type="port"
            disabled={!control.isEdit}
            formRef={control.formRef}
          />
        </Form.Item>
        <Form.Item
          name="password"
          validateStatus={control.passwordError ? "error" : undefined}
        >
          <FormInput
            inititalValue={
              control.data.ucp && control.currentContract
                ? control.currentContract.password
                : control.data.password
            }
            label={control.formatMessage({
              id: "machines.more.settings.password",
            })}
            onInputChecker={control.onCheckData}
            type="password"
            formRef={control.formRef}
            disabled={!control.isEdit || control.data.ucp}
            placeholder={
              control.data.ucp && control.currentContract
                ? control.currentContract.password
                : control.data.password
            }
          />
        </Form.Item>
        <Form.Item
          className={styles.label}
          colon={false}
          label={
            <span className={styles.use_controller_password}>
              {control.formatMessage({
                id: "machines.more.settings.password_switch",
              })}
            </span>
          }
          name="ucp"
          // initialValue={control.data.ucp}
          valuePropName="checked"
        >
          <Switch
            size="small"
            checked={control.data.ucp ?? false}
            onChange={control.onChangeContractPass}
            disabled={!control.isEdit}
            tabIndex={-1}
          />
          {control.ucpError && (
            <div className={styles.ucpError}>{control.ucpError}</div>
          )}
        </Form.Item>
      </Form>
      {
        control.isEdit && (
          <div className={styles.controls}>
            <Button
              htmlType="button"
              size="small"
              onClick={control.onResetForm}
              tabIndex={-1}
              variant="outlined"
            >
              {control.formatMessage({
                id: "machines.more.settings.data_fields.reset",
              })}
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={control.formRef.current?.submit}
              loading={control.saveLoading}
              tabIndex={-1}
              variant="solid"
            >
              {control.formatMessage({
                id: "machines.more.settings.data_fields.save",
              })}
            </Button>
          </div>
        )
      }
      {control.isAdmin && (
        <Flex align="end" vertical style={{ width: "100%" }}>
          <Button size="small" type="text" onClick={control.addendToComment}>
            <PlusOutlined />
            {control.formatMessage({ id: "machines.more.settings.data_fields.append_to_comment" })}
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
